/**
 * <mr-carousel> — scroll-snap carousel with real buttons.
 *
 * The scrolling itself is pure CSS (scroll-snap on the track). This component
 * only adds the controls: prev/next on desktop, dots on mobile, and it keeps
 * both in sync with whatever the user scrolls to. No dependencies.
 *
 * Markup contract (see snippets/mr-carousel.liquid):
 *   <mr-carousel>
 *     <div data-mr-track> ...slides... </div>
 *     <button data-mr-prev> <button data-mr-next>   (optional)
 *     <div data-mr-dots><button aria-current>…</div> (optional)
 *   </mr-carousel>
 *
 * When every slide fits in a row the section renders data-fits="true" and the
 * track stops being a scroller — the controls below are then inert and hidden.
 */
class MrCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-mr-track]');
    if (!this.track) return;

    this.prev = this.querySelector('[data-mr-prev]');
    this.next = this.querySelector('[data-mr-next]');
    this.dots = Array.from(this.querySelectorAll('[data-mr-dot]'));

    this.prev?.addEventListener('click', () => this.#step(-1));
    this.next?.addEventListener('click', () => this.#step(1));

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.#goTo(index));
    });

    this.onScroll = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.#sync());
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.observer = new ResizeObserver(() => this.#sync());
    this.observer.observe(this.track);

    this.#sync();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  get #slides() {
    return Array.from(this.track.children);
  }

  /** Distance from one snap point to the next, gap included. */
  get #stride() {
    const [first, second] = this.#slides;
    if (!first) return 0;
    if (second) return second.offsetLeft - first.offsetLeft;
    return first.getBoundingClientRect().width;
  }

  #step(direction) {
    const stride = this.#stride;
    if (!stride) return;
    this.track.scrollBy({
      left: stride * direction,
      behavior: this.#reducedMotion ? 'auto' : 'smooth',
    });
  }

  #goTo(index) {
    const slide = this.#slides[index];
    if (!slide) return;
    this.track.scrollTo({
      left: slide.offsetLeft - this.#slides[0].offsetLeft,
      behavior: this.#reducedMotion ? 'auto' : 'smooth',
    });
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #sync() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    // Sub-pixel rounding makes an exact comparison unreliable at either end.
    const maxScroll = scrollWidth - clientWidth;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft >= maxScroll - 1;

    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd;

    if (this.dots.length) {
      const stride = this.#stride;
      const active = stride ? Math.round(scrollLeft / stride) : 0;
      const clamped = Math.min(Math.max(active, 0), this.dots.length - 1);
      this.dots.forEach((dot, index) => {
        dot.setAttribute('aria-current', index === clamped ? 'true' : 'false');
      });
    }
  }
}

if (!customElements.get('mr-carousel')) {
  customElements.define('mr-carousel', MrCarousel);
}
